import React from "react";
import useReveal, { useCountUp } from "../hooks/useReveal";

const MILESTONES = [
  {
    year: "1989",
    title: "Founded",
    body: "Anfield Services Limited is formed to provide technical downhole fishing services for the drilling and workover operations of the oil and gas industry.",
  },
  {
    year: "1996",
    title: "Expansion",
    body: "The company substantially increases its inventory of tools and moves into its purpose-built facility at Otaheite Industrial Park, South Oropouche.",
  },
  {
    year: "Today",
    title: "Approved supplier",
    body: "ASL is accepted and approved to provide wellbore intervention specialists, rental tools and associated equipment to local and international operators across the region.",
  },
];

function YearsCounter() {
  const [ref, value] = useCountUp(100);
  return (
    <span ref={ref} className="h-display text-6xl text-flame-500 lg:text-7xl">
      {value}+
    </span>
  );
}

export default function About() {
  const headRef = useReveal();
  const listRef = useReveal();

  return (
    <section id="about" className="relative overflow-hidden bg-ink-950 py-24 lg:py-32">
      {/* Blueprint grid + a warm wash bleeding in from the right */}
      <div className="absolute inset-0 bg-grid bg-grid opacity-40" />
      <div className="absolute -right-40 top-1/3 h-[30rem] w-[30rem] rounded-full bg-flame-600/10 blur-[130px]" />

      <div className="relative mx-auto max-w-shell px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          {/* Left column — the pitch */}
          <div ref={headRef} className="reveal lg:col-span-5">
            <p className="kicker mb-6">01 — About</p>
            <h2 className="h-display text-5xl sm:text-6xl lg:text-7xl">
              Three decades
              <br />
              <span className="text-flame-500">under pressure</span>
            </h2>

            <p className="mt-8 text-steel-300">
              Anfield Services Limited (ASL) was formed in 1989 to provide
              technical Downhole Fishing Services for the Drilling and Work over
              Operations of the Oil and Gas Industry. The company substantially
              increased its inventory of tools in 1996 and moved into its new
              facility in South Oropouche, Trinidad.
            </p>
            <p className="mt-5 text-steel-300">
              The owners of Anfield Services Limited have between them 100+
              years of experience in the provision of technical and engineering
              supervision, fabrication, maintenance, application of tools and
              procedures used in Drilling and Work-over activities.
            </p>
            <p className="mt-5 text-steel-300">
              We have implemented our Quality, Health, Safety and Environment
              Management System (QHSEMS) to help us develop a safe culture to
              prevent accidents on our facilities and worksites.
            </p>

            {/* Headline number */}
            <div className="mt-12 flex items-end gap-5 border-t border-white/10 pt-8">
              <YearsCounter />
              <p className="pb-2 font-mono text-[11px] uppercase leading-relaxed tracking-wide2 text-steel-400">
                Years of combined
                <br />
                experience among our owners
              </p>
            </div>
          </div>

          {/* Right column — milestone record */}
          <div ref={listRef} className="reveal lg:col-span-7 lg:pt-4">
            <ol className="relative border-l border-white/10 pl-8 lg:pl-12">
              {MILESTONES.map((item, i) => (
                <li
                  key={item.year}
                  className="group relative pb-12 last:pb-0"
                  style={{ "--reveal-delay": `${i * 120}ms` }}
                >
                  {/* Node on the rail */}
                  <span className="absolute -left-[calc(2rem+5px)] top-2 h-[9px] w-[9px] rotate-45 border border-flame-500 bg-ink-950 transition-colors duration-300 group-hover:bg-flame-500 lg:-left-[calc(3rem+5px)]" />
                  <div className="flex flex-wrap items-baseline gap-4">
                    <span className="h-display text-4xl text-white lg:text-5xl">
                      {item.year}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-kicker text-flame-500">
                      {item.title}
                    </span>
                  </div>
                  <p className="mt-3 max-w-xl text-steel-400">{item.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
